import React, { useState, MouseEvent, useEffect, useRef, useCallback } from 'react'
import Popover from '@mui/material/Popover'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import ConciergeFloatingIcon from './components/ConciergeFloatingIcon'
import ChatsContainer from '../Chat/ChatsContainer'
import MessageBar from '../Chat/MessageBar'
import ChatHeader from '../Chat/ChatHeader'
import { Theme } from '@mui/material/styles'
import { AppState } from 'redux/config/store'
import { ChatTypes } from '../Chat/types'
import {
  openChatRoomFetchChats,
  postChat,
  addAIConciergeChat,
  setTypingAnimationComplete,
  startNewThread,
  addAnyNewChats,
} from './conciergeAIActions'
import { openRealtimeWebSocket, closeWebSocket } from './conciergeAIService'
import { resetConciergeSession } from './conciergeAISlice'
import { getPlatformType } from 'shared/Router/Utils'
import { useLocation } from 'react-router-dom'

const useStyles = makeStyles((_theme: Theme) => ({
  card: {
    borderRadius: 12,
    width: '45vw',
    height: '95vh',
    '@media (max-width: 1200px)': {
      width: '55vw',
    },
    '@media (max-width: 960px)': {
      width: '75vw',
    },
    '@media (max-width: 600px)': {
      width: '100vw',
    },
    '@media (max-width: 360px)': {
      width: '100vw',
    },
  },
  mainChatContainer: {
    position: 'relative',
    height: '100%',
  },
  header: {
    position: 'absolute',
    zIndex: 99,
    width: '100%',
  },
  chat: {
    maxHeight: '95vh !important',
  },
  messageBar: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
  },
}))

const RECONNECT_DELAY_MS = 2000

const ConciergeChat: React.FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [showChatIcon, setShowChatIcon] = useState(true)
  const {
    chats,
    numberOfUnseenMessages,
    state,
    chatRoom,
    isGettingResponse,
    threadId,
  } = useSelector((state: AppState) => state.conciergeAI)
  const websocket = useRef<WebSocket | null>(null)
  const intentionallyClosed = useRef(false)
  const reconnectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const connecting = useRef(false)
  const chatRoomRef = useRef(chatRoom)
  const threadIdRef = useRef(threadId)
  const chatsRef = useRef(chats)
  chatRoomRef.current = chatRoom
  threadIdRef.current = threadId
  chatsRef.current = chats

  const open = Boolean(anchorEl)
  const id = open ? 'tutor-chat' : undefined
  const location = useLocation()
  const platformType = getPlatformType()
  const platformRef = useRef(platformType)

  const clearReconnect = () => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current)
      reconnectTimeout.current = null
    }
  }

  const catchUpMissedChats = useCallback(() => {
    const roomId = chatRoomRef.current
    const currentThreadId = threadIdRef.current
    if (!roomId || !currentThreadId) {
      return
    }
    dispatch(addAnyNewChats(chatsRef.current, roomId, currentThreadId))
  }, [dispatch])

  const connectSocket = useCallback(async () => {
    const roomId = chatRoomRef.current
    if (!roomId || connecting.current) {
      return
    }
    if (
      websocket.current &&
      (websocket.current.readyState === WebSocket.OPEN ||
        websocket.current.readyState === WebSocket.CONNECTING)
    ) {
      return
    }
    connecting.current = true
    intentionallyClosed.current = false
    try {
      const ws = await openRealtimeWebSocket(roomId)
      websocket.current = ws
      // Pub/sub is fire-and-forget; catch up anything published while disconnected.
      catchUpMissedChats()
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data?.type === 'ping') {
            return
          }
        } catch {
          // Non-JSON payloads fall through to the saga parser
        }
        dispatch(addAIConciergeChat(event.data))
      }
      ws.onclose = () => {
        websocket.current = null
        if (!intentionallyClosed.current && chatRoomRef.current) {
          clearReconnect()
          reconnectTimeout.current = setTimeout(() => {
            void connectSocket()
          }, RECONNECT_DELAY_MS)
        }
      }
    } catch (error) {
      console.error('Error opening concierge websocket:', error)
      if (!intentionallyClosed.current && chatRoomRef.current) {
        clearReconnect()
        reconnectTimeout.current = setTimeout(() => {
          void connectSocket()
        }, RECONNECT_DELAY_MS)
      }
    } finally {
      connecting.current = false
    }
  }, [catchUpMissedChats, dispatch])

  const disconnectSocket = useCallback(() => {
    intentionallyClosed.current = true
    clearReconnect()
    if (websocket.current) {
      void closeWebSocket(websocket.current)
      websocket.current = null
    }
  }, [])

  useEffect(() => {
    if (platformRef.current === platformType) {
      return
    }
    platformRef.current = platformType
    disconnectSocket()
    dispatch(resetConciergeSession())
  }, [location.pathname, platformType, disconnectSocket, dispatch])

  useEffect(() => {
    if (chatRoom === null) {
      dispatch(openChatRoomFetchChats())
    }
  }, [chatRoom, dispatch])

  useEffect(() => {
    if (chatRoom) {
      void connectSocket()
    }
    return () => {
      disconnectSocket()
    }
  }, [chatRoom, connectSocket, disconnectSocket])

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    setShowChatIcon(false)
    void connectSocket()
  }

  const handleClose = () => {
    setAnchorEl(null)
    setShowChatIcon(true)
  }

  const isOwnChat = (chat: ChatTypes) => {
    return !chat.isAiResponse
  }

  const startNewThreadHere = () => {
    dispatch(startNewThread())
  }

  return (
    <div>
      <ConciergeFloatingIcon
        onChatIconClick={handleClick}
        showChatIcon={showChatIcon}
        tooltip='Chat with AI'
        numberOfUnseenMessages={numberOfUnseenMessages}
        anchorEl={anchorEl}
        isLoading={state === 'loading'}
      />
      <Popover
        id={id}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        classes={{ paper: classes.card }}
      >
        <div className={classes.mainChatContainer}>
          <div className={classes.header}>
            <ChatHeader
              displayName='AI Concierge'
              onCloseButtonClick={handleClose}
              onNewThreadButtonClick={() => startNewThreadHere()}
              chatState={state}
            />
          </div>
          <ChatsContainer
            chats={chats}
            isOwnChat={isOwnChat}
            className={classes.chat}
            renderMarkdown={true}
            onTypingAnimationComplete={(chatId: string) => {
              dispatch(setTypingAnimationComplete(chatId))
            }}
          />
          <div className={classes.messageBar}>
            {state !== 'loading' && (
              <MessageBar
                onMessageSend={(message: string) => {
                  dispatch(postChat(chatRoom, message, threadId))
                }}
                showAttachment={false}
                showEmoji={false}
                isGettingResponse={isGettingResponse}
              />
            )}
          </div>
        </div>
      </Popover>
    </div>
  )
}

export default React.memo(ConciergeChat)
